import { PageController } from '@client/components/_core/layout/PageController';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { useAdminModulePermissions } from '@client/hooks/admin/useAdminModulePermissions';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { createFileRoute, Navigate } from '@tanstack/react-router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { type AppSetting, AppSettingForm } from './AppSettingForm';

export const Route = createFileRoute('/admin-console/advanced/app-settings/all/add')({
    component: AddAppSettingRoute,
    staticData: {
        presentation: 'overlay',
    },
});

function AddAppSettingRoute() {
    const { isReadOnly } = useAdminModulePermissions('system');

    if (isReadOnly) {
        return <Navigate to="/admin-console/advanced/app-settings/all" />;
    }

    return (
        <PageController route={Route}>
            <AddAppSetting />
        </PageController>
    );
}

function AddAppSetting() {
    const { trpc, trpcUtils } = useTrpc();
    const [open, setOpen] = useState(true);
    const navigate = useNavigate();
    const { mutate: createAppSetting, isPending: isCreating } = trpc.admin.appSetting.createAppSetting.useMutation({
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const handleSubmit = (values: AppSetting) => {
        const key = 'create_setting';
        message.loading({ content: 'Creating…', key });

        createAppSetting(
            {
                name: values.name ?? '',
                value: values.value ?? '',
                format: values.format ?? 'PLAIN_TEXT',
            },
            {
                onSuccess: () => {
                    message.success({ content: 'Created', key });
                    trpcUtils.admin.appSetting.listAppSettings.invalidate();
                    setOpen(false);
                },
                onError: (error) => {
                    message.error(error.message || 'Failed to create app setting');
                },
            },
        );
    };

    return (
        <AppSettingForm
            open={open}
            mode="add"
            data={{
                name: '',
                value: '',
                format: 'PLAIN_TEXT',
            }}
            state={match({ isCreating })
                .with({ isCreating: true }, () => 'submitting' as const)
                .otherwise(() => 'ready' as const)}
            onSubmit={handleSubmit}
            onCloseFinished={() => {
                navigate({ to: '/admin-console/advanced/app-settings/all' });
            }}
        />
    );
}
